import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { clampBlockDimensionMm } from '../block-configurator/dimension-ops'

interface RulerDimensionInputProps {
  valueMm: number
  orientation: 'horizontal' | 'vertical'
  disabled?: boolean
  onChange: (mm: number) => void
}

export function RulerDimensionInput({ valueMm, orientation, disabled, onChange }: RulerDimensionInputProps) {
  const { t } = useTranslation()
  const label = t(orientation === 'horizontal' ? 'canvas.labelSize.width' : 'canvas.labelSize.height')
  const testId = orientation === 'horizontal' ? 'ruler-horizontal-input' : 'ruler-vertical-input'

  const [draft, setDraft] = useState(String(valueMm))
  const committedRef = useRef(false)

  // Re-seed from upstream when the prop changes (e.g. after undo/floor-clamp)
  useEffect(() => {
    setDraft(String(valueMm))
    committedRef.current = false
  }, [valueMm])

  const commit = (raw: string) => {
    if (committedRef.current) return
    const parsed = parseFloat(raw)
    if (Number.isNaN(parsed)) return
    committedRef.current = true
    onChange(clampBlockDimensionMm(parsed))
  }

  return (
    <input
      type="number"
      min={1}
      max={300}
      step={0.5}
      aria-label={label}
      data-testid={testId}
      value={draft}
      disabled={disabled}
      onChange={e => {
        committedRef.current = false
        setDraft(e.target.value)
      }}
      onBlur={() => commit(draft)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          commit(draft)
          e.currentTarget.blur()
        }
      }}
      className={[
        'w-14 rounded border border-stone-300 px-1 py-0.5 text-center font-mono text-[10px]',
        'dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-stone-100 dark:disabled:bg-stone-800',
      ].join(' ')}
    />
  )
}
